import React from 'react';
import { Box, TextField, Typography, Alert } from '@mui/material';

import { TemplateInputAction } from '../../../types';

interface TemplateInputActionEditorProps {
  action: TemplateInputAction;
  onChange: (action: TemplateInputAction) => void;
}

const TemplateInputActionEditor: React.FC<TemplateInputActionEditorProps> = ({
  action,
  onChange,
}) => {
  const handleChange = (field: keyof TemplateInputAction['data'], value: string) => {
    onChange({
      ...action,
      data: {
        ...action.data,
        [field]: value,
      },
    });
  };

  // Extract variable names from template
  const extractVariables = (template: string): string[] => {
    const matches = template.match(/\{\{([^,}]+)(?:,([^}]+))?\}\}/g) || [];
    const uniqueVars = new Set<string>();
    matches.forEach((match) => {
      const varName = match.match(/\{\{([^,}]+)/)?.[1] || '';
      uniqueVars.add(varName.trim());
    });
    return Array.from(uniqueVars);
  };

  const variables = extractVariables(action.data.template);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <TextField
        label="Template"
        value={action.data.template}
        onChange={(e) => handleChange('template', e.target.value)}
        multiline
        rows={2}
        fullWidth
        helperText="Use {{variableName,placeholder text}} to create input fields"
        placeholder="I want to {{action,e.g., create a campaign}} for {{cause,e.g., education}}"
      />

      {variables.length > 0 && (
        <Alert severity="info">
          <Typography variant="body2" gutterBottom>
            <strong>Variables detected:</strong>
          </Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 0 }}>
            {variables.map((variable) => (
              <li key={variable}>
                <code>{variable}</code> - This will be saved to context and can be used as{' '}
                <code>{`{{${variable}}}`}</code> in subsequent actions
              </li>
            ))}
          </Typography>
        </Alert>
      )}

      <Alert severity="success">
        <Typography variant="body2">
          <strong>Example:</strong> &quot;I want to{' '}
          {'{{action,e.g., volunteer}} for {{hours,e.g., 4}} hours'}&quot; will create a form with
          two input fields.
        </Typography>
      </Alert>
    </Box>
  );
};

export default TemplateInputActionEditor;
